import {
  LITERAL_COMPARISON_FUNCTIONS,
  FRACTION_TO_PERCENTAGE_FUNCTION,
  PERCENTAGE_FROM_FRACTION_SUB_TYPE,
  TYPE_FAMILIES,
} from "../types/constants";
import {
  filterValueMultiplier,
  scaleFilterValue,
  subTypeValueAffixes,
  unscaleFilterValue,
} from "./numberUtils";

/** Returns the sub-type of an operand that is a numeric column or a Fraction to Percentage function, or null. */
function operandSubType(operand, availableColumns) {
  if (operand?.func === FRACTION_TO_PERCENTAGE_FUNCTION) {
    return PERCENTAGE_FROM_FRACTION_SUB_TYPE;
  }
  if (operand?.func === "VAR") {
    const column = availableColumns.find(
      (c) => c.internalName === operand.name
    );
    return TYPE_FAMILIES.NUMBER.includes(column?.type)
      ? column.subType ?? null
      : null;
  }
  return null;
}

/** Returns the prefix and suffix shown around a number literal compared by func with otherOperand. */
export function comparisonLiteralAffixes(
  func,
  otherOperand,
  availableColumns = []
) {
  return subTypeValueAffixes(
    LITERAL_COMPARISON_FUNCTIONS.includes(func)
      ? operandSubType(otherOperand, availableColumns)
      : null
  );
}

/** Returns the factor a number literal compared by func with otherOperand is shown at relative to its saved value. */
export function comparisonLiteralMultiplier(
  func,
  otherOperand,
  availableColumns = []
) {
  if (!LITERAL_COMPARISON_FUNCTIONS.includes(func)) return 1;
  return filterValueMultiplier(operandSubType(otherOperand, availableColumns));
}

/** Returns a saved literal as shown, multiplying a number by multiplier. */
export function shownLiteral(value, multiplier) {
  return typeof value === "number"
    ? scaleFilterValue(value, multiplier)
    : value;
}

/** Returns a shown literal as saved, dividing a number by multiplier. */
export function savedLiteral(value, multiplier) {
  return typeof value === "number"
    ? unscaleFilterValue(value, multiplier)
    : value;
}

/** Returns the next pair expression with each number literal re-saved so its shown value is kept when the other operand changes. */
export function rescaleComparisonLiterals(
  previous,
  next,
  availableColumns = []
) {
  const rescale = (literal, previousOther, nextOther) => {
    if (typeof literal !== "number") return literal;
    const before = comparisonLiteralMultiplier(
      previous.func,
      previousOther,
      availableColumns
    );
    const after = comparisonLiteralMultiplier(
      next.func,
      nextOther,
      availableColumns
    );
    if (before === after) return literal;
    return savedLiteral(shownLiteral(literal, before), after);
  };
  return {
    ...next,
    a: rescale(next.a, previous.b, next.b),
    b: rescale(next.b, previous.a, next.a),
  };
}
